import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { DocenteService } from "service/DocenteService";
import ModalDocente from "components/Modals/ModalDocente";
import FSelect from "components/Select/FSelect";
import CustomPagination from "components/Pagination/Pagination";

function Docente() {
  const notificationAlertRef = useRef(null);
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [listDocentes, setListDocentes] = useState([]);
  const [listFiltered, setListFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEspecialidad, setFilterEspecialidad] = useState("Todos");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocente, setSelectedDocente] = useState(null);

  // Loading state
  const [loadingBuscar, setLoadingBuscar] = useState(false);

  // Confirm modal states
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ mensaje: "", onConfirm: null });

  const docenteService = new DocenteService();

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setSelectedDocente(null);
  };

  const getDocentes = async () => {
    setLoadingBuscar(true);
    try {
      const response = await docenteService.getDocentes();
      const data = response?.data || [];
      setListDocentes(data);
      applyFilters(data, searchQuery, filterEspecialidad);
    } catch (error) {
      console.error("Error cargando docentes:", error);
      notify("danger", "Error al cargar los docentes");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const applyFilters = (data, query, especialidad) => {
    let filtered = data;

    if (especialidad !== "Todos") {
      filtered = filtered.filter(item => item.especialidad === especialidad);
    }

    if (query) {
      const q = query.toLowerCase();
      filtered = filtered.filter(item =>
        (item.documentoidentidad || "").toLowerCase().includes(q) ||
        (item.nombres || "").toLowerCase().includes(q) ||
        (item.apellidopaterno || "").toLowerCase().includes(q) ||
        (item.apellidomaterno || "").toLowerCase().includes(q)
      );
    }

    setListFiltered(filtered);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(listDocentes, query, filterEspecialidad);
    setCurrentPage(1);
  };

  const handleFilterEspecialidad = (e) => {
    const especialidad = e.target.value;
    setFilterEspecialidad(especialidad);
    applyFilters(listDocentes, searchQuery, especialidad);
    setCurrentPage(1);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = listFiltered.slice(indexOfFirstItem, indexOfLastItem);

  const handleEdit = (docente) => {
    setSelectedDocente(docente);
    setModalOpen(true);
  };

  const handleToggleEstado = async (docente) => {
    const nuevoEstado = docente.estado === "1" ? "0" : "1";
    const confirmMsg = nuevoEstado === "1" ? "¿Desea dar de alta al docente?" : "¿Desea dar de baja al docente?";

    setConfirmData({
      mensaje: confirmMsg,
      onConfirm: async () => {
        setConfirmModal(false);
        try {
          const res = await docenteService.updateDocente({ ...docente, estado: nuevoEstado });
          if (res?.confirmation === "success") {
            getDocentes();
          }
        } catch (error) {
          console.error("Error cambiando estado:", error);
        }
      },
    });
    setConfirmModal(true);
  };

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Administración de Docentes</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button
                      color="link"
                      className="btn-icon btn-round"
                      title="Configuración"
                    >
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                {/* Zona 2: Barra de filtros */}
                <Row className="align-items-end mb-4">
                  <Col md="7">
                    <FormGroup className="mb-0">
                      <Label for="search">Búsqueda por DNI, Nombres o Apellidos</Label>
                      <Input
                        type="text"
                        id="search"
                        placeholder="Ingrese términos de búsqueda..."
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ height: "40px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup className="mb-0">
                      <Label for="especialidad">Especialidad</Label>
                      <FSelect
                        name="especialidad"
                        id="especialidad"
                        value={filterEspecialidad}
                        onChange={handleFilterEspecialidad}
                        options={[
                          { value: "Todos", label: "TODAS LAS ESPECIALIDADES" },
                          { value: "Ciencias", label: "CIENCIAS" },
                          { value: "Letras", label: "LETRAS" },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={getDocentes}
                        disabled={loadingBuscar}
                        style={{ height: "40px", padding: "0" }}
                        title="Buscar"
                      >
                        {loadingBuscar ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones */}
                <Row className="mb-4">
                  <Col md="6">
                    {/* Espacio reservado para acciones masivas */}
                  </Col>
                  <Col md="6" className="text-right">
                    <Button
                      color="primary"
                      onClick={toggleModal}
                      className="btn-fill"
                    >
                      <i className="tim-icons icon-simple-add mr-2" />
                      Agregar Docente
                    </Button>
                  </Col>
                </Row>

                <hr className="my-4" />

                <Table>
                  <thead className="text-primary">
                    <tr>
                      <th>DNI</th>
                      <th>Apellidos y Nombres</th>
                      <th>Especialidad</th>
                      <th>Estado</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{row.documentoidentidad}</td>
                        <td>
                          {`${row.apellidopaterno} ${row.apellidomaterno}, ${row.nombres}`}
                        </td>
                        <td>{row.especialidad}</td>
                        <td>
                          {row.estado === "1" ? (
                            <span className="text-success">Activo</span>
                          ) : (
                            <span className="text-danger">Inactivo</span>
                          )}
                        </td>
                        <td className="text-right">
                          <Button
                            color="info"
                            size="sm"
                            className="btn-icon btn-link"
                            title="Editar"
                            onClick={() => handleEdit(row)}
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <Button
                            color={row.estado === "1" ? "danger" : "success"}
                            size="sm"
                            className="btn-icon btn-link"
                            title={row.estado === "1" ? "Dar de baja" : "Dar de alta"}
                            onClick={() => handleToggleEstado(row)}
                          >
                            <i className={`tim-icons ${row.estado === "1" ? "icon-arrow-down" : "icon-arrow-up"}`} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {listFiltered.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center">No se encontraron registros</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <CustomPagination
                  itemsPerPage={itemsPerPage}
                  totalItems={listFiltered.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <ModalDocente
        isOpen={modalOpen}
        toggle={toggleModal}
        selectedDocente={selectedDocente}
        onSave={getDocentes}
      />

      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} size="sm" centered>
        <ModalHeader toggle={() => setConfirmModal(false)}>Confirmar acción</ModalHeader>
        <ModalBody><p>{confirmData.mensaje}</p></ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancelar</Button>
          <Button color="danger" onClick={confirmData.onConfirm}>Confirmar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default Docente;
